"use client";

import * as React from "react";
import { PhoneCall, CircleQuestionMark as HelpCircle, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const faqs = [{
  question: "What should I do if I have a burst pipe?",
  answer: "Turn off your main water supply immediately to prevent further water flow. Then, request service through our marketplace. Ask your pro about prompt arrival options to secure active leaks."
}, {
  question: "How do pros find hidden leaks?",
  answer: "Local plumbers use specialized inspection equipment, including thermal tools and pressure testing devices, to locate leaks behind walls or beneath flooring."
}, {
  question: "Is it better to repair or replace my water heater?",
  answer: "If your water heater is older or frequent repairs are required, replacement can be a practical long-term option. Your local pro can evaluate your unit and provide recommendations."
}, {
  question: "What's the difference between water heater types?",
  answer: "Traditional tank heaters store hot water continuously. Tankless models heat water on demand for increased efficiency, while heat pump units offer energy-efficient electric water heating. Ask your pro for a tailored estimate."
}, {
  question: "How can I prevent frozen pipes in winter?",
  answer: "Ensure exposed pipes are properly insulated. When temperatures drop below freezing, allow a slow drip from your faucets and open under-sink cabinet doors to allow warm air to circulate around the plumbing."
}, {
  question: "Do pros offer maintenance agreements?",
  answer: "Many local plumbing pros offer seasonal inspection options. Routine checks can help identify potential issues early, extending the life of your plumbing systems."
}, {
  question: "How quickly can a plumber arrive?",
  answer: "Response times and arrival windows vary by contractor and schedule. Submit a request to check current availability with independent pros in your area."
}];
export default function PlumbingFAQ() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: faqRef,
    isVisible: isFaqVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="plumbingservicefaq" className="bg-background py-16 md:py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
        {}
        <div ref={headerRef} className={`text-center mb-12 md:mb-16 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Badge variant="secondary" className="mb-4">
            <HelpCircle className="mr-1.5 size-3.5" />
            Plumbing FAQ
          </Badge>
          <h2 className="font-default text-3xl md:text-4xl lg:text-5xl font-bold text-foreground tracking-tight mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            Find answers to common plumbing questions, from urgent leak steps to preventative care.
          </p>
        </div>

        {}
        <div ref={faqRef} className={`bg-card rounded-xl p-6 md:p-8 shadow-sm border border-border transition-all duration-700 delay-100 ease-out ${isFaqVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <Accordion type="single" collapsible className="w-full">
            {faqs.map((faq, index) => <AccordionItem key={index} value={`item-${index}`} data-index={index}>
                <AccordionTrigger className="text-base md:text-lg font-semibold text-foreground hover:text-primary transition-colors">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>)}
          </Accordion>
        </div>

        {}
        <div ref={ctaRef} className={`mt-12 md:mt-16 text-center transition-all duration-700 delay-200 ease-out ${isCtaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="bg-primary/5 rounded-2xl p-8 md:p-10 border border-primary/10 flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-left">
              <h3 className="font-default text-xl md:text-2xl font-bold text-foreground mb-2">
                Still have questions or need immediate help?
              </h3>
              <p className="text-muted-foreground">
                Independent local plumbers in our network are ready to help with your repair needs.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 shrink-0">
              <Button asChild size="lg" className="w-full sm:w-auto">
                <Link to="#bookingrequest">
                  Request a Quote
                  <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-background">
                <a href="tel:+14708479076">
                  <PhoneCall className="mr-2 size-4" />
                  (470) 847-9076
                </a>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}